/**
 * Real-time notifications for contact requests
 * Uses WebSocket (primary) with polling fallback for new pending requests
 * Includes notification sounds
 */

import { useEffect, useRef, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { contactRequestsApi, ContactRequest } from '@/services/api/contact-requests.api';
import { wsService } from '@/services/websocket.service';
import { playNotificationSound } from '@/utils/notification-sounds';

interface UseContactRequestNotificationsOptions {
  role: 'seller' | 'staff';
  interval?: number;
  enabled?: boolean;
}

export function useContactRequestNotifications({
  role,
  interval = 60_000,
  enabled = true,
}: UseContactRequestNotificationsOptions) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { language } = useLanguage();
  const knownIdsRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);

  const showBrowserNotification = useCallback((title: string, body: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, { body, icon: '/favicon.ico' });
    }
  }, []);

  const notify = useCallback((title: string, body: string) => {
    toast({ title, description: body, duration: 8000 });
    showBrowserNotification(title, body);
    playNotificationSound();
  }, [toast, showBrowserNotification]);

  useEffect(() => {
    if (enabled && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [enabled]);

  // WebSocket events
  useEffect(() => {
    if (!enabled || !user) return;

    wsService.connect();

    const handleNewContactRequest = (data: any) => {
      const isFr = language === 'fr';
      const title = isFr ? '📩 Nouvelle demande de contact' : '📩 New Contact Request';
      const body = isFr
        ? `${data.buyerName || 'Un acheteur'} souhaite contacter le vendeur pour "${data.dealName || data.dealId || ''}"`
        : `${data.buyerName || 'A buyer'} wants to contact the seller for "${data.dealName || data.dealId || ''}"`;

      notify(title, body);
      if (data.id) knownIdsRef.current.add(data.id);
    };

    const handleContactRequestUpdated = (data: any) => {
      const isFr = language === 'fr';
      if (data.status === 'accepted') {
        notify(
          isFr ? '✅ Demande acceptée' : '✅ Request Accepted',
          isFr ? 'La demande de contact a été acceptée' : 'The contact request has been accepted',
        );
      } else if (data.status === 'rejected') {
        notify(
          isFr ? '❌ Demande refusée' : '❌ Request Rejected',
          isFr ? 'La demande de contact a été refusée' : 'The contact request has been rejected',
        );
      }
    };

    const unsub1 = wsService.on('new_contact_request', handleNewContactRequest);
    const unsub2 = wsService.on('contact_request_updated', handleContactRequestUpdated);

    return () => {
      unsub1();
      unsub2();
    };
  }, [enabled, user, language, notify]);

  // Polling fallback
  useEffect(() => {
    if (!enabled || !user) return;

    const fetchAndNotify = async () => {
      try {
        const requests: ContactRequest[] =
          role === 'staff'
            ? await contactRequestsApi.getAll()
            : await contactRequestsApi.getReceived();

        const pendingRequests = requests.filter((r) => r.status === 'pending');

        if (!initializedRef.current) {
          pendingRequests.forEach((r) => knownIdsRef.current.add(r.id));
          initializedRef.current = true;
          return;
        }

        for (const r of pendingRequests) {
          if (!knownIdsRef.current.has(r.id)) {
            knownIdsRef.current.add(r.id);
            const isFr = language === 'fr';
            notify(
              isFr ? '📩 Nouvelle demande de contact' : '📩 New Contact Request',
              isFr
                ? `${r.buyerName || 'Un acheteur'} souhaite contacter le vendeur pour "${r.dealName || r.dealId}"`
                : `${r.buyerName || 'A buyer'} wants to contact the seller for "${r.dealName || r.dealId}"`,
            );
          }
        }

        const currentPendingIds = new Set(pendingRequests.map((r) => r.id));
        knownIdsRef.current.forEach((id) => {
          if (!currentPendingIds.has(id)) knownIdsRef.current.delete(id);
        });
      } catch {
        // Silently ignore
      }
    };

    fetchAndNotify();
    const timer = setInterval(fetchAndNotify, interval);
    return () => clearInterval(timer);
  }, [enabled, user, role, interval, language, notify]);
}
